import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck, MapPin, Clock, Users, ChevronRight, CalendarDays, Plane, Building2, Pencil, CalendarRange, User, Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import ReservaDetail from "@/pages/trips/ReservaDetail";
import GuardiaRota from "@/pages/guardias/GuardiaRota";

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtLong = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" }); } catch { return s; } };
const fmtShort = (s) => { try { return new Date(s + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" }); } catch { return s; } };

const TYPE_ICON = { hotel: Building2, vuelo: Plane, transfer: MapPin, alojamiento: Building2, actividad: MapPin };

// ===== Fila de actividad (clic = abrir/editar la reserva) =====
function ActivityRow({ a, tripId, showDate, onOpen }) {
  const Icon = TYPE_ICON[a.type] || MapPin;
  return (
    <button type="button"
      onClick={() => onOpen({ tripId, reservaId: a.id })}
      className="group flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
      data-testid={`guardias-act-${a.id}`} title="Abrir y editar la reserva">
      <div className={`flex shrink-0 flex-col items-center ${showDate ? "w-20" : "w-12"}`}>
        {showDate && <span className="text-[10px] font-medium capitalize text-primary">{fmtShort(a.date)}</span>}
        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold tabular-nums">{a.hour_entry || "—"}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-sm font-medium"><Icon className="h-3.5 w-3.5 text-primary" />{a.service}</p>
        <p className="truncate text-xs text-muted-foreground">{a.provider_name}{a.city ? ` · ${a.city}` : ""}</p>
      </div>
      {a.pax ? <Badge variant="outline" className="shrink-0 gap-1 text-[10px]"><Users className="h-3 w-3" />{a.pax}</Badge> : null}
      <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
}

// ===== Tarjeta de viaje (cabecera viajero + actividades) =====
function TripCard({ trip, activities, showDate, onOpen, onVer, testid }) {
  return (
    <Card className="overflow-hidden" data-testid={testid}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 border-b bg-muted/30 pb-3">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="truncate">{trip.main_traveler}</span>
            <Badge variant="outline" className="shrink-0 text-[10px]">{activities.length} act.</Badge>
          </CardTitle>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />{trip.destination || "—"}
            {trip.agent && <span className="ml-1">· {trip.agent}</span>}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 shrink-0 text-xs"
          onClick={() => onVer(trip.trip_id)} data-testid={`guardias-open-${trip.trip_id}`}>
          Ver viaje <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-1.5 p-3">
        {activities.map((a) => <ActivityRow key={a.id} a={a} tripId={trip.trip_id} showDate={showDate} onOpen={onOpen} />)}
      </CardContent>
    </Card>
  );
}

// ===== Modal con el editor COMPLETO de la reserva (reutiliza el de Trips) =====
function ReservaDialog({ target, onClose }) {
  const qc = useQueryClient();
  const open = !!target;
  const tripId = target?.tripId;
  const q = useQuery({
    queryKey: ["trip", tripId],
    queryFn: async () => (await api.get(`/trips/${tripId}`)).data,
    enabled: open && !!tripId,
  });
  const data = q.data;
  const reserva = (data?.reservas || []).find((r) => r.id === target?.reservaId);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] w-[96vw] max-w-6xl overflow-y-auto" data-testid="guardias-reserva-dialog">
        <DialogHeader className="sr-only"><DialogTitle>Editar reserva</DialogTitle></DialogHeader>
        {q.isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : !reserva ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No se ha encontrado la reserva.</p>
        ) : (
          <ReservaDetail
            tripId={tripId}
            trip={data.trip}
            reserva={reserva}
            recordatorios={data.recordatorios || []}
            onBack={onClose}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ["trip", tripId] });
              qc.invalidateQueries({ queryKey: ["guardias"] });
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function GuardiasPage() {
  const navigate = useNavigate();
  const [start, setStart] = useState(todayStr());
  const [end, setEnd] = useState(addDays(todayStr(), 2));
  const [agent, setAgent] = useState("all");
  const [groupBy, setGroupBy] = useState("day"); // "day" | "client"
  const [openReserva, setOpenReserva] = useState(null); // {tripId, reservaId}

  const onStart = (v) => { if (!v) return; setStart(v); if (v > end) setEnd(v); };
  const onEnd = (v) => { if (!v) return; setEnd(v); if (v < start) setStart(v); };
  const applyPreset = (n) => { const t = todayStr(); setStart(t); setEnd(addDays(t, n)); };
  const openTrip = (id) => navigate(`/app/trips?trip=${id}`);

  const today = todayStr();
  const presets = [
    { n: 0, label: "Hoy" },
    { n: 2, label: "3 días" },
    { n: 6, label: "Semana" },
  ];
  const activePreset = (n) => start === today && end === addDays(today, n);

  const agentsQ = useQuery({ queryKey: ["guardias-agents"], queryFn: async () => (await api.get("/guardias/agents")).data });
  const q = useQuery({ queryKey: ["guardias", start, end, agent], queryFn: async () => (await api.get("/guardias/dia", { params: { start, end, agent } })).data });
  const data = q.data || { days: [], totals: { days: 0, trips: 0, activities: 0 } };

  // Reagrupación por cliente (viajero): junta las actividades de todo el rango por viaje, ordenadas por día+hora.
  const clients = useMemo(() => {
    const map = new Map();
    for (const day of data.days) {
      for (const t of day.trips) {
        let c = map.get(t.trip_id);
        if (!c) { c = { ...t, activities: [] }; map.set(t.trip_id, c); }
        for (const a of t.activities) c.activities.push({ ...a, date: day.date });
      }
    }
    const arr = [...map.values()];
    for (const c of arr) {
      c.activities.sort((x, y) => `${x.date} ${x.hour_entry || "99:99"}`.localeCompare(`${y.date} ${y.hour_entry || "99:99"}`));
    }
    arr.sort((a, b) => (a.main_traveler || "").toLowerCase().localeCompare((b.main_traveler || "").toLowerCase()));
    return arr;
  }, [data.days]);

  return (
    <div className="space-y-6" data-testid="guardias-page">
      <Tabs defaultValue="operativa">
        <TabsList data-testid="guardias-tabs">
          <TabsTrigger value="operativa" data-testid="guardias-tab-operativa"><CalendarDays className="mr-1.5 h-4 w-4" />Operativa diaria</TabsTrigger>
          <TabsTrigger value="rota" data-testid="guardias-tab-rota"><Shield className="mr-1.5 h-4 w-4" />Calendario de guardias</TabsTrigger>
        </TabsList>
        <TabsContent value="operativa" className="mt-4 space-y-6">
      {/* Cabecera + controles */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight">
            <ShieldCheck className="h-6 w-6 text-primary" /> Guardias
          </h1>
          <p className="flex items-center gap-1.5 text-sm capitalize text-muted-foreground">
            <CalendarRange className="h-3.5 w-3.5" />{fmtLong(start)} — {fmtLong(end)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {presets.map((p) => (
            <Button key={p.n} size="sm" variant={activePreset(p.n) ? "default" : "outline"}
              onClick={() => applyPreset(p.n)} data-testid={`guardias-preset-${p.label.toLowerCase().replace(/\s/g, "")}`}>
              {p.label}
            </Button>
          ))}
          <div className="flex items-center gap-1.5 rounded-md border px-2 py-1">
            <span className="text-xs font-medium text-muted-foreground">Desde</span>
            <Input type="date" value={start} max={end} onChange={(e) => onStart(e.target.value)}
              className="h-8 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid="guardias-start" />
            <span className="text-xs font-medium text-muted-foreground">Hasta</span>
            <Input type="date" value={end} min={start} onChange={(e) => onEnd(e.target.value)}
              className="h-8 w-[140px] border-0 p-0 shadow-none focus-visible:ring-0" data-testid="guardias-end" />
          </div>
          <Select value={agent} onValueChange={setAgent}>
            <SelectTrigger className="h-9 w-48" data-testid="guardias-agent-filter"><SelectValue placeholder="Responsable" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {(agentsQ.data?.agents || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totales + conmutador de agrupación */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm" data-testid="guardias-total-days">
            <CalendarDays className="h-3.5 w-3.5" /> {data.totals.days} días con actividad
          </Badge>
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm" data-testid="guardias-total-trips">
            <Plane className="h-3.5 w-3.5" /> {data.totals.trips} viajes
          </Badge>
          <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 text-sm" data-testid="guardias-total-activities">
            <Clock className="h-3.5 w-3.5" /> {data.totals.activities} actividades
          </Badge>
        </div>
        <div className="flex items-center rounded-lg border bg-muted/40 p-0.5" data-testid="guardias-groupby">
          <Button size="sm" variant={groupBy === "day" ? "default" : "ghost"} className="h-8 gap-1.5 text-xs"
            onClick={() => setGroupBy("day")} data-testid="guardias-view-days">
            <CalendarDays className="h-3.5 w-3.5" />Por días
          </Button>
          <Button size="sm" variant={groupBy === "client" ? "default" : "ghost"} className="h-8 gap-1.5 text-xs"
            onClick={() => setGroupBy("client")} data-testid="guardias-view-client">
            <User className="h-3.5 w-3.5" />Por cliente
          </Button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="space-y-3"><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : data.days.length === 0 ? (
        <Card data-testid="guardias-empty">
          <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><CalendarDays className="h-6 w-6" /></div>
            <p className="font-semibold">Sin actividades en este rango</p>
            <p className="text-sm text-muted-foreground">Ningún viaje activo tiene servicios programados entre estas fechas.</p>
          </CardContent>
        </Card>
      ) : groupBy === "day" ? (
        // ===== Vista POR DÍAS =====
        <div className="space-y-8" data-testid="guardias-by-day">
          {data.days.map((day) => (
            <section key={day.date} className="space-y-3" data-testid={`guardias-day-${day.date}`}>
              <div className="sticky top-0 z-10 -mx-1 flex items-center gap-3 border-b bg-background/95 px-1 py-2 backdrop-blur">
                <h2 className="font-heading text-lg font-bold capitalize tracking-tight">{fmtLong(day.date)}</h2>
                <Badge variant="outline" className="gap-1 text-[11px]">
                  <Plane className="h-3 w-3" />{day.trips.length} · <Clock className="h-3 w-3" />{day.count}
                </Badge>
              </div>
              <div className="space-y-3">
                {day.trips.map((t) => (
                  <TripCard key={`${day.date}-${t.trip_id}`} trip={t} activities={t.activities} showDate={false}
                    onOpen={setOpenReserva} onVer={openTrip} testid={`guardias-trip-${t.trip_id}`} />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        // ===== Vista POR CLIENTE =====
        <div className="space-y-3" data-testid="guardias-by-client">
          {clients.map((c) => (
            <TripCard key={c.trip_id} trip={c} activities={c.activities} showDate
              onOpen={setOpenReserva} onVer={openTrip} testid={`guardias-client-${c.trip_id}`} />
          ))}
        </div>
      )}

      <ReservaDialog target={openReserva} onClose={() => setOpenReserva(null)} />
        </TabsContent>
        <TabsContent value="rota" className="mt-4">
          <GuardiaRota />
        </TabsContent>
      </Tabs>
    </div>
  );
}
